import React, { useContext, useEffect } from 'react';
import { FormControlLabel, Checkbox } from '@mui/material';
import { connect } from 'react-redux';
import { Form, Field } from 'react-final-form';
import {
  kycGotoPrevPage as kycGotoPrevPageRoutine,
  kycGotoNextPage as kycGotoNextPageRoutine,
  kycSetDraftStockExchanges,
  kycSubmitCustomerDetails as kycSubmitCustomerDetailsRoutine,
} from 'spa/actions/VerificationActions';
import VerificationConstants, {
  KYCPages,
  UBO_NOT_PUBLICLY_LISTED_VALUE,
} from 'spa/constants/VerificationConstants';
import ActionButtons from 'spa/components/CustomerVerification/V2/ActionButtons';
import {
  customerKycHasPrevPagesSelector,
  customerKycLoadingUiSelector,
  customerKycDraftCompanyDetailsSelector,
  customerKycDraftHasUBOSelector,
  customerKycDraftStockExchangesSelector,
  customerKycSubmitLoadingSelector,
  customerRiskAssessmentAnswersSelector,
} from 'spa/selectors/CustomerSelectors';
import { required } from 'spa/components/form/validate';
import { KYCContext } from 'spa/context/KYCContext';
import Heading from './Heading';

const StockExchangeForm = ({
  draftCompanyDetails,
  draftHasUBO,
  draftStockExchanges,
  riskAssessmentAnswers,
  isNavigating,
  isSubmitting,
  hasPrevPages,
  loadPrevPage,
  setDraftStockExchanges,
  gotoNextPage,
  submitCustomerDetails,
  stockExchangeOptions,
}) => {
  const kycContext = useContext(KYCContext);

  useEffect(() => {
    if (!draftCompanyDetails) {
      gotoNextPage({
        nextPage: KYCPages.COMPANY_DETAILS_FORM,
        forceNextPage: true,
        purgeHistory: true,
        kycContext,
      });
    } else if (!draftHasUBO) {
      gotoNextPage({
        nextPage: KYCPages.HAS_UBO_FORM,
        forceNextPage: true,
        purgeHistory: true,
        kycContext,
      });
    }
  }, [draftCompanyDetails, draftHasUBO, gotoNextPage, kycContext]);

  const atLeastOneRequired = (value) => (value && value.length ? undefined : required());

  const onFormSubmit = (values, form) => {
    const registeredFields = form.getRegisteredFields();
    const isNoneSelected = (values.stock_exchanges || []).includes(UBO_NOT_PUBLICLY_LISTED_VALUE);

    setDraftStockExchanges({ values, registeredFields });

    if (isNoneSelected) {
      gotoNextPage({
        nextPage: KYCPages.BENEFICIAL_OWNERS_FORM,
        forceNextPage: true,
        kycContext,
      });
      return;
    }

    const draftValues = {
      ...((draftCompanyDetails && draftCompanyDetails.values) || {}),
      ...((draftHasUBO && draftHasUBO.values) || {}),
      ...values,
    };
    const draftRegisteredFields = [
      ...new Set([
        ...((draftCompanyDetails && draftCompanyDetails.registeredFields) || []),
        ...((draftHasUBO && draftHasUBO.registeredFields) || []),
        ...registeredFields,
      ]),
    ];
    submitCustomerDetails({
      formName: VerificationConstants.STOCK_EXCHANGES_FORM,
      registeredFields: draftRegisteredFields,
      values: draftValues,
      kycContext,
    });
  };

  const wasPreviouslyAnsweredNotListed =
    riskAssessmentAnswers && riskAssessmentAnswers.is_listed_on_exchange === false;

  return (
    <Form
      onSubmit={onFormSubmit}
      initialValues={
        (draftStockExchanges && draftStockExchanges.values) || {
          stock_exchanges: wasPreviouslyAnsweredNotListed
            ? [UBO_NOT_PUBLICLY_LISTED_VALUE]
            : riskAssessmentAnswers && riskAssessmentAnswers.stock_exchanges,
        }
      }
    >
      {({ handleSubmit, hasValidationErrors, values }) => {
        const selectedExchanges = values.stock_exchanges || [];
        const isNoneSelected = selectedExchanges.includes(UBO_NOT_PUBLICLY_LISTED_VALUE);
        return (
          <form onSubmit={handleSubmit} name={VerificationConstants.STOCK_EXCHANGES_FORM}>
            <Heading
              text="Is your company publicly listed on any stock exchange? Select all that apply."
              hasDivider
            />
            <Field name="stock_exchanges" validate={atLeastOneRequired}>
              {({ input }) => {
                const selected = input.value || [];
                const noneSelected = selected.includes(UBO_NOT_PUBLICLY_LISTED_VALUE);

                const toggleExchange = (value) => (event) => {
                  const { checked } = event.target;
                  const withoutNone = selected.filter((v) => v !== UBO_NOT_PUBLICLY_LISTED_VALUE);
                  input.onChange(
                    checked
                      ? [...withoutNone, value]
                      : withoutNone.filter((v) => v !== value)
                  );
                };

                const toggleNone = (event) => {
                  input.onChange(event.target.checked ? [UBO_NOT_PUBLICLY_LISTED_VALUE] : []);
                };

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', alignContent: 'start' }}>
                    {stockExchangeOptions.map(({ value, label }) => (
                      <FormControlLabel
                        key={value}
                        style={{ marginBottom: '1rem' }}
                        control={
                          <Checkbox
                            checked={selected.includes(value)}
                            onChange={toggleExchange(value)}
                            disabled={noneSelected}
                          />
                        }
                        label={label}
                      />
                    ))}
                    <FormControlLabel
                      control={<Checkbox checked={noneSelected} onChange={toggleNone} />}
                      label={<span style={{ fontWeight: 600 }}>Company is not publicly listed</span>}
                    />
                  </div>
                );
              }}
            </Field>
            <ActionButtons
              secondaryText="Back"
              primaryText={isNoneSelected ? 'Next' : 'Save and Next'}
              isLoading={isNavigating || isSubmitting}
              isDisabled={hasValidationErrors}
              type="submit"
              withBack={hasPrevPages}
              onSecondaryClick={() => loadPrevPage()}
            />
          </form>
        );
      }}
    </Form >
  );
};

const mapStateToProps = (state) => ({
  draftCompanyDetails: customerKycDraftCompanyDetailsSelector(state),
  draftHasUBO: customerKycDraftHasUBOSelector(state),
  draftStockExchanges: customerKycDraftStockExchangesSelector(state),
  riskAssessmentAnswers: customerRiskAssessmentAnswersSelector(state),
  isNavigating: customerKycLoadingUiSelector(state),
  isSubmitting: customerKycSubmitLoadingSelector(state),
  hasPrevPages: customerKycHasPrevPagesSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  loadPrevPage: () => dispatch(kycGotoPrevPageRoutine.trigger()),
  setDraftStockExchanges: (payload) => dispatch(kycSetDraftStockExchanges(payload)),
  gotoNextPage: (payload) => dispatch(kycGotoNextPageRoutine.trigger(payload)),
  submitCustomerDetails: (payload) => dispatch(kycSubmitCustomerDetailsRoutine.trigger(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StockExchangeForm);
